import {
  DataTableColumn,
  FormDialog as CustomFormDialog,
  FormDialogMode,
  formDialogProps,
  InputStyle,
  Section
} from '@globaleris/ui.library'
import React, { useRef, useCallback, useState } from 'react'

/**
 * useFormDialogV2 - Enhanced version of useFormDialog hook - Recommended to use instead of useFormDialog
 *
 * PROBLEMS WITH ORIGINAL useFormDialog:
 * 1. Form data loss when definition updates: When the form definition (inputs/sections) changes dynamically,
 *    the original hook would reset all form data, causing users to lose their entered values
 * 2. No dynamic form updates: The original hook couldn't handle scenarios where form fields need to be
 *    updated based on user selections (dependent Selects, conditional fields, etc. See invoiceCycles.tsx as example)
 * 3. State synchronization issues: No reliable way to preserve form state during form definition changes
 *
 * FIXES PROVIDED BY V2:
 * 1. Form data preservation: Uses formDataRef to maintain form data across definition updates
 * 2. Dynamic form updates: updateCurrentDefinition() allows real-time form changes while preserving data
 * 3. Better state management: Separate tracking of form open state and current form ID
 * 4. Data persistence: Form data is preserved when sections/inputs are updated dynamically
 *
 * Author: Ezequiel Scigolini
 * Date: October 2025
 */

export interface FormDialogDefinitionPropsV2 {
  id: string
  inputs?: DataTableColumn[]
  sections?: Section[]
}

export interface FormDialogHookPropsV2 {
  definition: FormDialogDefinitionPropsV2
  inputs?: DataTableColumn[]
  sections?: Section[]
  inputData?: any
  open?: boolean
  isWaiting?: boolean
  onClose: (formData: any) => void
  title?: string
  subtitle?: string | React.ReactNode
  mode?: FormDialogMode
  okBtnLabel?: string
  cancelBtnLabel?: string
  closeBtnLabel?: string
  inputType?: InputStyle
}

export interface FormDialogHookResultV2 {
  openForm: (props: FormDialogHookPropsV2) => void
  getFormData: (id: any) => any
  updateFormSections: (sections: Section[]) => void
  updateFormData: (data: any) => void
  updateCurrentDefinition: (definition: FormDialogDefinitionPropsV2) => void
  closeForm: () => void
  setFormWaiting: (isWaiting: boolean) => void
  formProps: formDialogProps
  CustomFormDialog?: any
  setFormSubtitle: (subtitle: string | React.ReactNode) => void
  setFormMode: (mode: FormDialogMode) => void
}

const initialFormDialogProps = {
  definition: { id: '', inputs: [], sections: [] },
  inputs: [],
  inputData: null,
  mode: FormDialogMode.ADD,
  open: false,
  onClose: () => {}
}

export const useFormDialogV2 = () => {
  const [formProps, setFormProps] = useState<formDialogProps>(initialFormDialogProps)
  const [isOpen, setIsOpen] = useState(false)
  const formDataRef = useRef<any>(null)
  const currentFormIdRef = useRef<string>('')

  const updateFormSections = useCallback(
    (sections: Section[]) => {
      console.log('updateFormSections called:', {
        isOpen,
        currentFormId: currentFormIdRef.current,
        sectionsLength: sections.length,
        currentFormData: formDataRef.current
      }) // Debug log
      if (isOpen && currentFormIdRef.current) {
        console.log('Updating form sections in V2 hook, preserving data:', formDataRef.current) // Debug log
        setFormProps(prev => ({
          ...prev,
          sections: sections,
          inputData: formDataRef.current // Preserve current form data
        }))
      } else {
        console.log('Not updating sections - form not open or no current form ID') // Debug log
      }
    },
    [isOpen]
  )

  const updateFormData = useCallback((data: any) => {
    formDataRef.current = data
    setFormProps(prev => ({
      ...prev,
      inputData: data
    }))
  }, [])

  const updateCurrentDefinition = useCallback(
    (definition: FormDialogDefinitionPropsV2) => {
      if (isOpen && currentFormIdRef.current === definition.id) {
        console.log('Updating current definition with preserved data:', formDataRef.current)
        setFormProps(prev => ({
          ...prev,
          inputs: definition.inputs || [],
          sections: definition.sections || [],
          inputData: formDataRef.current // Preserve current form data
        }))
      }
    },
    [isOpen]
  )

  const getFormData = useCallback((id: any) => {
    return id === currentFormIdRef.current ? formDataRef.current : null
  }, [])

  const openForm = useCallback((props: FormDialogHookPropsV2) => {
    currentFormIdRef.current = props.definition.id
    formDataRef.current = props.inputData || null
    setIsOpen(true)

    setFormProps({
      inputs: props.definition.inputs || [],
      sections: props.definition.sections || [],
      inputData: props.inputData,
      mode: props.mode || FormDialogMode.ADD,
      open: true,
      title: props.title,
      subtitle: props.subtitle,
      isWaiting: props.isWaiting || false,
      okBtnLabel: props.okBtnLabel,
      cancelBtnLabel: props.cancelBtnLabel,
      closeBtnLabel: props.closeBtnLabel,
      inputType: props.inputType,
      onClose: (formData: any) => {
        // Store the form data before closing
        formDataRef.current = formData
        // Call the original onClose
        props.onClose(formData)
        // Clean up
        setIsOpen(false)
        currentFormIdRef.current = ''
        formDataRef.current = null
      }
    })
  }, [])

  const closeForm = useCallback(() => {
    setIsOpen(false)
    currentFormIdRef.current = ''
    formDataRef.current = null
    setFormProps(initialFormDialogProps)
  }, [])

  const setFormWaiting = useCallback((isWaiting: boolean) => {
    setFormProps(prev => ({
      ...prev,
      isWaiting: isWaiting
    }))
  }, [])

  const setFormSubtitle = useCallback((subtitle: string | React.ReactNode) => {
    setFormProps(prev => ({
      ...prev,
      subtitle: subtitle
    }))
  }, [])

  const setFormMode = useCallback((mode: FormDialogMode) => {
    setFormProps(prev => ({
      ...prev,
      mode: mode
    }))
  }, [])

  return {
    openForm,
    updateFormSections,
    updateFormData,
    updateCurrentDefinition,
    getFormData,
    closeForm,
    setFormWaiting,
    formProps,
    CustomFormDialog,
    setFormSubtitle,
    setFormMode
  } as FormDialogHookResultV2
}
